import { useSizeTracker } from "../hooks/use-size-tracker";
import { cn } from "../utils/cn";
import type { WidthAndHeight } from "../width-and-height";

/**
 * A React component that automatically measures its own width and height,
 * and passes these dimensions to its child function. The child is only rendered
 * when both width and height are non-zero, preventing rendering issues during initial layout.
 *
 * @param props.className - Optional additional class names for the container div.
 * @param props.children - A render prop function that receives the current width and height,
 *   and returns a React element to render.
 *
 * @example
 * <AutoSizer>
 *   {({ width, height }) => <SomeComponent width={width} height={height} />}
 * </AutoSizer>
 */
const AreaSize = (props: {
    readonly className?: string;
    readonly children: (params: WidthAndHeight) => React.JSX.Element;
}) => {
    const [sizeRef, size] = useSizeTracker();
    let avoidRenderingChildren = false;
    if (size.width === 0 && size.height === 0) {
        avoidRenderingChildren = true;
    }
    return (
        <div
            ref={sizeRef}
            className={cn("size-full flex-1", props.className)}
            style={{ overflow: "visible" }}>
            {avoidRenderingChildren ? null : props.children(size)}
        </div>
    );
};

export { AreaSize };
